import { getStudioAccessToken } from '@/services/auth-provider'
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'
import type {
  PreviewDraft,
  PreviewShareRole,
  PreviewReviewSnapshot,
  ReviewItem,
  ReviewPatch,
} from './preview-review-store'
import { isPreviewReviewSnapshot } from './preview-review-store'

const DEFAULT_PREVIEW_REVIEW_PROXY_BASE_URL = '/api/preview-review'

export type PlatformPreviewShareRole = 'viewer' | 'commenter' | 'editor'

export interface PlatformPreviewShareRecord {
  schemaVersion: 'cogo.project-share.v1'
  token: string
  projectId: string
  slug: string | null
  role: PlatformPreviewShareRole
  permissions: {
    canView: boolean
    canComment: boolean
    canRequestEdit: boolean
    canApplySimpleEdit: boolean
  }
  previewUrl: string
  shareUrl: string
  evidenceRefs: string[]
  createdBy: string
  createdAt: string
  expiresAt: string | null
}

export interface PlatformPreviewShareResult {
  attempted: boolean
  ok: boolean
  status?: number
  share?: PlatformPreviewShareRecord
  error?: string
}

export interface PlatformReviewSyncResult {
  attempted: boolean
  ok: boolean
  status?: number
  response?: unknown
  error?: string
}

export interface PreviewReviewServerSyncOptions {
  baseUrl?: string
  platformBaseUrl?: string
  shareToken?: string | null
  getAccessToken?: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

export interface PreviewReviewServerSyncResult {
  attempted: boolean
  ok: boolean
  status?: number
  snapshot?: PreviewReviewSnapshot
  error?: string
}

function envValue(key: string): string | undefined {
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> }
  return meta.env?.[key]
}

function configuredBaseUrl(options: PreviewReviewServerSyncOptions = {}): string | null {
  const raw = options.baseUrl ?? envValue('VITE_PREVIEW_REVIEW_URL') ?? DEFAULT_PREVIEW_REVIEW_PROXY_BASE_URL
  const trimmed = raw?.trim().replace(/\/$/, '')
  return trimmed || null
}

function configuredPlatformBaseUrl(options: PreviewReviewServerSyncOptions = {}): string | null {
  const raw = options.platformBaseUrl ?? getCogoPlatformBase()
  const trimmed = raw?.trim().replace(/\/$/, '')
  return trimmed || null
}

async function getSupabaseAccessToken(): Promise<string | null> {
  return getStudioAccessToken()
}

async function readSnapshotResponse(response: Response): Promise<PreviewReviewServerSyncResult> {
  const text = await response.text()
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parsed = null
  }
  const snapshot = (parsed as { snapshot?: unknown } | null)?.snapshot
  if (response.ok && isPreviewReviewSnapshot(snapshot)) {
    return {
      attempted: true,
      ok: true,
      status: response.status,
      snapshot,
    }
  }
  return {
    attempted: true,
    ok: false,
    status: response.status,
    error: typeof (parsed as { error?: unknown } | null)?.error === 'string'
      ? String((parsed as { error: string }).error)
      : text || `Preview review sync failed: ${response.status}`,
  }
}

function previewUrl(baseUrl: string, previewId: string, shareToken?: string | null, suffix = ''): string {
  const path = `${baseUrl}/v1/previews/${encodeURIComponent(previewId)}`
  const target = `${path}${suffix}`
  if (!shareToken) return target
  const params = new URLSearchParams({ share: shareToken })
  return `${target}?${params.toString()}`
}

function platformUrl(baseUrl: string, suffix: string): string {
  return `${baseUrl}${suffix.startsWith('/') ? suffix : `/${suffix}`}`
}

async function readJsonResponse(response: Response): Promise<{ body: unknown; error?: string }> {
  const text = await response.text()
  if (!text) return { body: null }
  try {
    return { body: JSON.parse(text) as unknown }
  } catch {
    return { body: text, error: text }
  }
}

function platformError(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const record = body as { error?: unknown; message?: unknown }
    if (typeof record.error === 'string') return record.error
    if (typeof record.message === 'string') return record.message
  }
  return fallback
}

export function platformRoleForPreviewShare(role: PreviewShareRole): PlatformPreviewShareRole {
  if (role === 'edit-preview' || role === 'owner') return 'editor'
  if (role === 'comment' || role === 'suggest') return 'commenter'
  return 'viewer'
}

function targetForPlatform(targetRef: ReviewItem['targetRef']): { blockId?: string; elementId?: string; role?: string } {
  return {
    ...(targetRef.blockId ? { blockId: targetRef.blockId } : {}),
    ...(targetRef.nodeId ? { elementId: targetRef.nodeId } : {}),
    ...(targetRef.label ? { role: targetRef.label } : {}),
  }
}

function simpleEditForPlatform(patch: ReviewPatch): { kind: 'replaceText' | 'replaceImage'; value: string; alt?: string } | null {
  if (patch.op === 'replaceText') {
    return { kind: 'replaceText', value: patch.nextText }
  }
  if (patch.op === 'replaceImage') {
    return { kind: 'replaceImage', value: patch.nextAssetRef, alt: patch.altText }
  }
  return null
}

export async function createPlatformPreviewShare(
  input: {
    projectId: string
    slug?: string | null
    role?: PlatformPreviewShareRole
    expiresAt?: string | null
    evidenceRefs?: string[]
  },
  options: PreviewReviewServerSyncOptions = {},
): Promise<PlatformPreviewShareResult> {
  const baseUrl = configuredPlatformBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  const fetcher = options.fetchImpl ?? fetch
  const token = await (options.getAccessToken ?? getSupabaseAccessToken)().catch(() => null)
  if (!token) return { attempted: false, ok: false, error: 'Sign in required for Platform preview sharing.' }

  try {
    const response = await fetcher(platformUrl(baseUrl, '/api/project/share-preview'), {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(input),
    })
    const { body } = await readJsonResponse(response)
    const share = (body as { share?: unknown } | null)?.share
    if (response.ok && share && typeof share === 'object') {
      return {
        attempted: true,
        ok: true,
        status: response.status,
        share: share as PlatformPreviewShareRecord,
      }
    }
    return {
      attempted: true,
      ok: false,
      status: response.status,
      error: platformError(body, `Platform preview share failed: ${response.status}`),
    }
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function appendPreviewReviewItemToPlatform(
  projectId: string,
  input: {
    reviewItem: ReviewItem
    draft?: PreviewDraft
  },
  options: PreviewReviewServerSyncOptions = {},
): Promise<PlatformReviewSyncResult> {
  const baseUrl = configuredPlatformBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  if (!options.shareToken) {
    return { attempted: false, ok: false, error: 'Share token required for Platform reviewer sync.' }
  }
  const fetcher = options.fetchImpl ?? fetch
  const target = targetForPlatform(input.reviewItem.targetRef)
  if (!target.blockId) {
    return {
      attempted: false,
      ok: false,
      error: 'Platform reviewer sync requires a blockId target.',
    }
  }

  const proposedPatch = input.reviewItem.proposedPatch
  const simpleEdit = proposedPatch ? simpleEditForPlatform(proposedPatch) : null
  const endpoint = simpleEdit
    ? '/api/project/simple-edit-request'
    : '/api/project/review-comment'
  const body = simpleEdit
    ? {
        projectId,
        shareToken: options.shareToken,
        target,
        edit: simpleEdit,
        apply: false,
      }
    : {
        projectId,
        shareToken: options.shareToken,
        target,
        text: input.reviewItem.body,
        authorId: input.reviewItem.createdByDisplay,
      }

  try {
    const response = await fetcher(platformUrl(baseUrl, endpoint), {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    })
    const { body: responseBody } = await readJsonResponse(response)
    return response.ok
      ? {
          attempted: true,
          ok: true,
          status: response.status,
          response: responseBody,
        }
      : {
          attempted: true,
          ok: false,
          status: response.status,
          response: responseBody,
          error: platformError(responseBody, `Platform reviewer sync failed: ${response.status}`),
        }
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function loadPreviewReviewSnapshotFromServer(
  previewId: string,
  options: PreviewReviewServerSyncOptions = {},
): Promise<PreviewReviewServerSyncResult> {
  const baseUrl = configuredBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  const fetcher = options.fetchImpl ?? fetch
  const token = await (options.getAccessToken ?? getSupabaseAccessToken)().catch(() => null)
  try {
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await fetcher(previewUrl(baseUrl, previewId, options.shareToken), { headers })
    return readSnapshotResponse(response)
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function savePreviewReviewSnapshotToServer(
  previewId: string,
  snapshot: PreviewReviewSnapshot,
  options: PreviewReviewServerSyncOptions = {},
): Promise<PreviewReviewServerSyncResult> {
  const baseUrl = configuredBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  const fetcher = options.fetchImpl ?? fetch
  const token = await (options.getAccessToken ?? getSupabaseAccessToken)().catch(() => null)
  if (!token) return { attempted: false, ok: false, error: 'Sign in required for preview review sync.' }
  try {
    const response = await fetcher(previewUrl(baseUrl, previewId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ snapshot }),
    })
    return readSnapshotResponse(response)
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function appendPreviewReviewItemToServer(
  previewId: string,
  input: {
    reviewItem: ReviewItem
    draft?: PreviewDraft
  },
  options: PreviewReviewServerSyncOptions = {},
): Promise<PreviewReviewServerSyncResult> {
  const baseUrl = configuredBaseUrl(options)
  if (!baseUrl) return { attempted: false, ok: false }
  if (!options.shareToken) {
    return { attempted: false, ok: false, error: 'Share token required for reviewer sync.' }
  }
  const fetcher = options.fetchImpl ?? fetch
  try {
    const response = await fetcher(previewUrl(baseUrl, previewId, options.shareToken, '/review-items'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    return readSnapshotResponse(response)
  } catch (error) {
    return {
      attempted: true,
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
